import React, { useState, useEffect } from "react";
import { Link } from "react-scroll";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  // Handle scroll detection - change color after hero section
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      // Try to get the actual hero section height
      const heroSection = document.getElementById("home");
      if (heroSection) {
        const heroHeight = heroSection.offsetHeight;
        setIsScrolled(scrollTop > heroHeight - 100); // Change 100px before end of hero
      } else {
        // Fallback to viewport height calculation
        const heroHeight = window.innerHeight * 0.8;
        setIsScrolled(scrollTop > heroHeight);
      }

      // Detect active section
      const sections = ["home", "services", "products", "about", "contact"];
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { to: "home", label: "Home" },
    { to: "services", label: "Services" },
    { to: "products", label: "Products" },
    { to: "about", label: "About" },
    { to: "contact", label: "Contact" },
  ];

  return (
    <motion.nav
      className={`fixed w-full z-50 transition-all duration-500 ease-out ${
        isScrolled
          ? "bg-white/80 backdrop-blur-sm shadow-sm border-gray-200"
          : "bg-white/10 backdrop-blur-md"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      {/* Smooth frosted edges: glass -> transparent -> glass using a full-width gradient overlay (lg only) */}
      <div className="pointer-events-none hidden lg:block absolute inset-0">
        <div
          aria-hidden
          className="h-full w-full"
          style={{
            // Subtle frosted overlay: lighter at hero, slightly stronger after scroll
            background: isScrolled
              ? "linear-gradient(to right, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.6) 30%, rgba(255,255,255,0.0) 100%)"
              : "linear-gradient(to right, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 40%, rgba(255,255,255,0.0) 100%)",
            backdropFilter: isScrolled ? "blur(6px)" : "blur(10px)",
            WebkitBackdropFilter: isScrolled ? "blur(6px)" : "blur(10px)",
            transition: "background 300ms ease, backdrop-filter 300ms ease",
          }}
        />
      </div>
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="home"
            smooth
            duration={500}
            offset={-80}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <motion.div
              className="p-1 rounded-xl transition-all duration-300"
              whileHover={{ scale: 1.05, rotate: 2 }}
              whileTap={{ scale: 0.95 }}
            >
              <img
                src="/LOGO.jpg"
                alt="Trust Import and Export"
                className="h-14 w-auto object-contain"
              />
            </motion.div>
            <span className="text-2xl font-bold text-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-yellow-700 to-brand-olive ">
              TRUST IMPEX
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item, index) => (
              <motion.div
                key={item.to}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={item.to}
                  smooth
                  duration={500}
                  offset={-80}
                  spy={true}
                  activeClass={`font-semibold text-brand-navy`}
                  className={`relative px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer group ${
                    activeSection === item.to
                      ? "text-brand-navy font-semibold"
                      : "text-gray-700"
                  }`}
                >
                  <span className="relative z-10">{item.label}</span>

                  {/* Hover Effect */}
                  <motion.div
                    className={`absolute inset-0 rounded-lg transition-all duration-300 bg-gradient-to-r from-brand-yellow/10 to-transparent`}
                    initial={{ opacity: 0, scale: 0.98 }}
                    whileHover={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                  />

                  {/* Active Indicator */}
                  <motion.div
                    className={`absolute bottom-0 left-1/2 h-0.5 rounded-full transition-all duration-300 bg-brand-yellow`}
                    animate={{
                      width: activeSection === item.to ? "80%" : "0%",
                      x: activeSection === item.to ? "-40%" : "0%",
                    }}
                    transition={{ duration: 0.3 }}
                  />
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <motion.div
            className="lg:hidden flex items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Menu"
              className={`p-2 rounded-xl transition-all duration-300 hover:bg-gray-100`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6 text-brand-navy transition-colors duration-300" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6 text-brand-navy transition-colors duration-300" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="lg:hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{
              backgroundColor: `rgba(255, 255, 255, 0.7)`,
              backdropFilter: `blur(12px)`,
              WebkitBackdropFilter: `blur(12px)`,
              borderTop: `1px solid rgba(255, 255, 255, 0.3)`,
            }}
          >
            <div className="py-6 space-y-2">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Link
                    to={item.to}
                    smooth
                    duration={500}
                    offset={-80}
                    spy={true}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-xl font-medium transition-all duration-300 cursor-pointer ${
                      activeSection === item.to
                        ? "text-brand-navy font-semibold bg-gray-100"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}

              {/* Mobile CTA */}
              <motion.div
                className="pt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
              >
                <Link
                  to="contact"
                  smooth
                  duration={500}
                  offset={-80}
                  onClick={() => setIsOpen(false)}
                ></Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
